import { supabase } from '../lib/supabase/client'
import { DOCUMENT_CONFIG, ERROR_MESSAGES, isFileTypeAllowed } from '../config/documentConfig'

interface UploadResult {
  success: boolean
  path?: string
  error?: string
  url?: string
  documentRecord?: EquipmentDocument
}

interface DocumentUploadParams {
  file: File
  customerName: string
  serialNumber: string
  equipmentId: string  // Required for database integration
  documentType?: string
  inspectionStatus?: string  // For control cards: OK, C, MO, RC, NC
}

interface EquipmentDocument {
  id: string
  equipment_id: string
  document_type: string
  file_name: string
  storage_path: string
  file_size?: number
  mime_type?: string
  version: string
  expiration_date?: string
  is_current: boolean
  created_at: string
  uploaded_by?: string
  uploaded_by_name?: string // Profile name from RPC function
}

// Utility functions for sanitizing folder names
const sanitizeForFolderName = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return DOCUMENT_CONFIG.FALLBACK_FOLDER_NAME
  }
  
  return input
    .toLowerCase()
    .trim()
    // First normalize Norwegian characters
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    || DOCUMENT_CONFIG.FALLBACK_FOLDER_NAME
}

const sanitizeFileName = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || ''
  const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
  const sanitizedName = nameWithoutExt
    .toLowerCase()
    // First normalize Norwegian characters
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z0-9.-]/g, '_')
  
  return extension ? `${sanitizedName}.${extension}` : sanitizedName
}

// Generate standardized filename based on document type
const generateStandardizedFileName = (documentType: string, serialNumber: string, originalFileName: string): string => {
  const extension = originalFileName.split('.').pop()?.toLowerCase() || 'pdf'
  const sanitizedSerial = sanitizeForFolderName(serialNumber)
  
  // Special handling for control_card and ndt_report - use the originalFileName if it looks like a report number
  if ((documentType === 'control_card' && originalFileName.match(/^STMI-\d+\.pdf$/)) ||
      (documentType === 'ndt_report' && originalFileName.match(/^(STMI|ET|MT|VT|PT|LT)-.+\.pdf$/i)) ||
      (documentType === 'thread_report' && originalFileName.match(/^(CTH|RSC)-.+\.pdf$/i)) ||
      (documentType === 'service_report' && originalFileName.match(/^SR-.+\.pdf$/i))) {
    console.log('🏷️ Using report number as filename for', documentType, ':', originalFileName)
    return originalFileName
  }
  
  // Map document types to Norwegian filename prefixes
  const getFilePrefix = (docType: string): string => {
    switch (docType) {
      case 'control_card':
      case 'Kontrollkort':
        return 'kontroll'
      case 'certificate':
      case 'Sertifikat':
        return 'sertifikat'
      case 'declaration_of_conformity':
      case 'Samsvarserklæring':
        return 'coc'
      case 'ndt_report':
      case 'NDT-rapport':
        return 'ndt_rapport'
      case 'thread_report':
      case 'Gjengerapport':
        return 'gjengerapport'
      case 'service_report':
      case 'Servicerapport':
        return 'servicerapport'
      case 'user_manual':
      case 'Brukermanual':
        return 'brukermanual'
      case 'other':
      case 'Annet':
        return 'dokument'
      default:
        return 'dokument'
    }
  }
  
  const filePrefix = getFilePrefix(documentType)
  return `${filePrefix}_${sanitizedSerial}.${extension}`
}

const generateDocumentPath = (customerName: string, serialNumber: string, fileName: string, documentType?: string, equipmentId?: string): string => {
  const sanitizedCustomer = sanitizeForFolderName(customerName)
  
  // Use standardized filename if document type is provided
  const finalFileName = documentType 
    ? generateStandardizedFileName(documentType, serialNumber, fileName)
    : sanitizeFileName(fileName)
  
  // ALWAYS use equipment_id as the folder to guarantee uniqueness
  // Serial numbers can be duplicated across companies or even within the same company
  // The equipment_id (UUID) is the only guaranteed unique identifier
  if (equipmentId) {
    // Path: {company}/{equipment_uuid}/{filename}
    return [sanitizedCustomer, equipmentId, finalFileName].join(DOCUMENT_CONFIG.FOLDER_SEPARATOR)
  }
  
  // Fallback for legacy calls without equipment_id (should not happen in normal flow)
  const sanitizedSerial = sanitizeForFolderName(serialNumber)
  console.warn('⚠️ Document upload without equipment_id - using serial number as fallback')
  return [sanitizedCustomer, sanitizedSerial, finalFileName].join(DOCUMENT_CONFIG.FOLDER_SEPARATOR)
}

// Map file types to Norwegian document types
const getDocumentTypeFromFile = (fileName: string, _fileType: string): string => {
  const lowerFileName = fileName.toLowerCase()
  
  if (lowerFileName.includes('kontrollkort') || lowerFileName.includes('control')) {
    return 'Kontrollkort'
  }
  if (lowerFileName.includes('sertifikat') || lowerFileName.includes('certificate')) {
    return 'Sertifikat'
  }
  if (lowerFileName.includes('ndt') || lowerFileName.includes('rapport')) {
    return 'NDT-rapport'
  }
  if (lowerFileName.includes('manual') || lowerFileName.includes('bruk')) {
    return 'Brukermanual'
  }
  if (lowerFileName.includes('service')) {
    return 'Servicerapport'
  }
  if (lowerFileName.includes('samsvar') || lowerFileName.includes('compliance') || lowerFileName.includes('coc')) {
    return 'Samsvarserklæring'
  }
  
  return 'Annet'  // Default fallback
}

// Convert Norwegian label back to enum value
const convertLabelToEnum = (label: string): string => {
  switch (label) {
    case 'Kontrollkort': return 'control_card'
    case 'Sertifikat': return 'certificate'
    case 'Samsvarserklæring': return 'declaration_of_conformity'
    case 'NDT-rapport': return 'ndt_report'
    case 'Servicerapport': return 'service_report'
    case 'Brukermanual': return 'user_manual'
    case 'Annet': return 'other'
    default: return 'other'
  }
}

export const uploadDocument = async ({
  file,
  customerName,
  serialNumber,
  equipmentId,
  documentType,
  inspectionStatus
}: DocumentUploadParams): Promise<UploadResult> => {
  try {
    if (import.meta.env.DEV) console.log('🔵 Starting upload process...')
    if (import.meta.env.DEV) console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type)
    if (import.meta.env.DEV) console.log('Customer:', customerName, 'Serial:', serialNumber, 'Equipment ID:', equipmentId)

    // Validate file
    if (!file) {
      if (import.meta.env.DEV) console.log('❌ No file selected')
      return { success: false, error: ERROR_MESSAGES.NO_FILE_SELECTED }
    }

    if (!equipmentId) {
      if (import.meta.env.DEV) console.log('❌ No equipment ID provided')
      return { success: false, error: 'Equipment ID is required' }
    }

    // Check file size
    if (file.size > DOCUMENT_CONFIG.MAX_FILE_SIZE) {
      if (import.meta.env.DEV) console.log('❌ File too large:', file.size, 'Max:', DOCUMENT_CONFIG.MAX_FILE_SIZE)
      return { success: false, error: ERROR_MESSAGES.FILE_TOO_LARGE }
    }

    // Check file type
    if (!isFileTypeAllowed(file.type)) {
      if (import.meta.env.DEV) console.log('❌ Invalid file type:', file.type)
      return { success: false, error: ERROR_MESSAGES.INVALID_FILE_TYPE }
    }

    // Use the passed document type (enum value) or auto-detect and convert to enum
    let finalDocumentType = documentType
    if (!finalDocumentType) {
      // Auto-detect returns Norwegian label, convert to enum value
      const detectedLabel = getDocumentTypeFromFile(file.name, file.type)
      finalDocumentType = convertLabelToEnum(detectedLabel)
    }
    if (import.meta.env.DEV) console.log('📋 Document type determined:', finalDocumentType)

    // Generate file path with standardized naming
    // Include equipmentId to ensure unique paths for equipment with similar serial numbers
    const filePath = generateDocumentPath(customerName, serialNumber, file.name, finalDocumentType, equipmentId)
    if (import.meta.env.DEV) console.log('📁 Generated path:', filePath)

    // Check Supabase client
    if (!supabase) {
      if (import.meta.env.DEV) console.log('❌ Supabase client not initialized')
      return { success: false, error: 'Supabase client not available' }
    }

    // Check authentication
    const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser()
    if (authError || !currentUser) {
      if (import.meta.env.DEV) console.log('❌ User not authenticated:', authError)
      return { success: false, error: 'Du må være logget inn for å laste opp dokumenter' }
    }

    if (import.meta.env.DEV) console.log('🚀 Starting Supabase upload...')
    
    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(DOCUMENT_CONFIG.BUCKET_NAME)
      .upload(filePath, file, {
        cacheControl: DOCUMENT_CONFIG.CACHE_CONTROL,
        upsert: true, // Allow overwriting existing files
        contentType: file.type || 'application/octet-stream' // Explicitly set content type
      })

    if (error) {
    console.error('❌ Upload error:', error)
      let errorMessage: string = ERROR_MESSAGES.UPLOAD_FAILED
      
      // Provide more specific error messages
      if (error.message.includes('row-level security')) {
        errorMessage = 'Ingen tilgang til å laste opp filer. Kontakt administrator.'
      } else if (error.message.includes('storage')) {
        errorMessage = 'Lagringsfeil. Prøv igjen senere.'
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'Nettverksfeil. Sjekk internettforbindelsen din.'
      } else {
        errorMessage = `${ERROR_MESSAGES.UPLOAD_FAILED}: ${error.message}`
      }
      
      return { success: false, error: errorMessage }
    }

    if (import.meta.env.DEV) console.log('✅ Upload successful:', data)

    // Get signed URL instead of public URL (bucket is private)
    const { data: signed } = await supabase.storage
      .from(DOCUMENT_CONFIG.BUCKET_NAME)
      .createSignedUrl(filePath, 60 * 60)

    // Get current user for uploaded_by
    const { data: { user } } = await supabase.auth.getUser()

    if (import.meta.env.DEV) console.log('📋 Document type:', finalDocumentType)

    // Use RPC function to handle document versioning atomically
    if (import.meta.env.DEV) console.log('💾 Creating database record with atomic versioning...')
    const standardizedFileName = filePath.split('/').pop() || file.name
    
    // Handle potential duplicate key constraint by checking if document already exists FOR THIS EQUIPMENT
    // Note: We check both storage_path AND equipment_id to avoid linking to wrong equipment
    // when serial numbers sanitize to the same folder name (e.g., "DING DONG" vs "DING-DONG" both become "ding_dong")
    const { data: existingDocArray } = await supabase
      .from('equipment_documents')
      .select('id, storage_path')
      .eq('storage_path', filePath)
      .eq('equipment_id', equipmentId)  // IMPORTANT: Only match documents for THIS equipment
      .limit(1)
    
    const existingDoc = existingDocArray && existingDocArray.length > 0 ? existingDocArray[0] : null
    
    let documentRecord
    let dbError
    
    if (existingDoc) {
      if (import.meta.env.DEV) console.log('📄 Document with same path exists for this equipment, updating existing record...')
      // Update existing document record instead of creating new one
      const { data, error } = await supabase
        .from('equipment_documents')
        .update({
          file_name: standardizedFileName,
          uploaded_by: user?.id
        })
        .eq('id', existingDoc.id)
        .select()
        .single()
      
      documentRecord = data
      dbError = error
    } else {
      // Create new document record
      if (inspectionStatus && finalDocumentType === 'control_card') {
        // For control cards with inspection status, handle versioning manually
        // First, deactivate any existing current documents of the same type
        const { error: updateError } = await supabase
          .from('equipment_documents')
          .update({ is_current: false })
          .eq('equipment_id', equipmentId)
          .eq('document_type', finalDocumentType)
          .eq('is_current', true)
        
        if (updateError) {
          console.error('❌ Error deactivating existing documents:', updateError)
          documentRecord = null
          dbError = updateError
        } else {
          // Then insert the new document with inspection status
          const { data, error } = await supabase
            .from('equipment_documents')
            .insert({
              equipment_id: equipmentId,
              document_type: finalDocumentType,
              file_name: standardizedFileName,
              storage_path: filePath,
              version: '1.0',
              inspection_status: inspectionStatus,
              uploaded_by: user?.id,
              is_current: true
            })
            .select()
            .single()
          
          documentRecord = data
          dbError = error
        }
      } else {
        // Use RPC function for other documents
        const { data, error } = await supabase.rpc('insert_equipment_document', {
          p_equipment_id: equipmentId,
          p_document_type: finalDocumentType,
          p_file_name: standardizedFileName,
          p_storage_path: filePath,
          p_version: '1.0'
        })
        
        // RPC function returns an array, so take the first (and only) item
        documentRecord = data && data.length > 0 ? data[0] : null
        dbError = error
      }
    }

    if (dbError) {
      console.error('❌ Database error:', dbError)
      // Try to clean up uploaded file if it's a new upload that failed
      if (!existingDoc) {
        await supabase.storage.from(DOCUMENT_CONFIG.BUCKET_NAME).remove([filePath])
      }
      return { success: false, error: `Database error: ${dbError.message}` }
    }

    if (import.meta.env.DEV) console.log('✅ Database record created:', documentRecord)
    if (import.meta.env.DEV) console.log('📋 Document record for linking:', {
      id: documentRecord?.id,
      hasRecord: !!documentRecord,
      type: documentRecord?.document_type
    })

    return {
      success: true,
      path: filePath,
      url: signed?.signedUrl,
      documentRecord
    }

  } catch (error) {
    console.error('💥 Document upload error:', error)
    
    if (error instanceof Error && error.message.includes('timeout')) {
      return { 
        success: false, 
        error: 'Upload timed out. Please check your internet connection and try again.'
      }
    }
    
    return { 
      success: false, 
      error: error instanceof Error ? error.message : ERROR_MESSAGES.UNKNOWN_ERROR
    }
  }
}

/**
 * Link an existing document to multiple equipment (e.g., sub-equipment)
 * This uses a linking table approach since storage_path has UNIQUE constraint
 */
export const linkDocumentToSubEquipment = async (
  originalDocumentId: string,
  subEquipmentIds: string[]
): Promise<{ success: boolean; error?: string; linkedCount?: number }> => {
  try {
    if (subEquipmentIds.length === 0) {
      return { success: true, linkedCount: 0 }
    }

    // Use fallback approach since equipment_document_links table doesn't exist
    return await linkDocumentByDuplication(originalDocumentId, subEquipmentIds)
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

/**
 * Fallback: Link documents by creating copies with unique storage paths
 * This works with the current UNIQUE constraint on storage_path
 */
async function linkDocumentByDuplication(
  originalDocumentId: string,
  subEquipmentIds: string[]
): Promise<{ success: boolean; error?: string; linkedCount?: number }> {
  try {
    // Get the original document details
    const { data: originalDoc, error: fetchError } = await supabase
      .from('equipment_documents')
      .select('*')
      .eq('id', originalDocumentId)
      .single()

    if (fetchError || !originalDoc) {
      return { success: false, error: `Failed to fetch original document: ${fetchError?.message}` }
    }

    const linkPromises = subEquipmentIds.map(async (equipmentId) => {
      try {
        // Create a virtual storage path by adding equipment ID suffix
        const virtualStoragePath = `${originalDoc.storage_path}#${equipmentId}`

        // Check if a virtual document with this storage path already exists for this equipment
        const { data: existingDocs, error: checkError } = await supabase
          .from('equipment_documents')
          .select('id, storage_path, file_name')
          .eq('equipment_id', equipmentId)
          .eq('storage_path', virtualStoragePath)

        if (checkError) {
          console.error(`❌ Error checking existing documents for equipment ${equipmentId}:`, checkError);
          return { equipmentId, success: false, error: checkError.message };
        }

        if (existingDocs && existingDocs.length > 0) {
          // Check if the existing virtual document is already pointing to the same original document
          const existingVirtualDoc = existingDocs[0]; // Should only be one with this exact path
          if (existingVirtualDoc.file_name === originalDoc.file_name) {
            return { equipmentId, success: true, alreadyLinked: true }
          }
        }

        // Clean up any old virtual documents of the same type for this equipment
        try {
          await supabase
            .from('equipment_documents')
            .delete()
            .eq('equipment_id', equipmentId)
            .eq('document_type', originalDoc.document_type)
            .like('storage_path', `%#${equipmentId}`)
            .neq('storage_path', virtualStoragePath);
        } catch (cleanupException) {
          // Don't fail the entire process for cleanup errors
        }

        let insertError;
        try {
          const result = await supabase
            .from('equipment_documents')
            .insert({
              equipment_id: equipmentId,
              document_type: originalDoc.document_type,
              file_name: originalDoc.file_name,
              storage_path: virtualStoragePath,
              version: originalDoc.version,
              uploaded_by: originalDoc.uploaded_by
            })
            .select()
          
          insertError = result.error;
        } catch (insertException) {
          return { equipmentId, success: false, error: insertException instanceof Error ? insertException.message : 'Insert exception' };
        }

        if (insertError) {
          // If it's a unique constraint violation, try to update existing instead
          if (insertError.code === '23505' || insertError.message?.includes('duplicate key')) {
            try {
              const { error: updateError } = await supabase
                .from('equipment_documents')
                .update({
                  file_name: originalDoc.file_name,
                  version: originalDoc.version,
                  uploaded_by: originalDoc.uploaded_by,
                  document_type: originalDoc.document_type
                })
                .eq('equipment_id', equipmentId)
                .eq('storage_path', virtualStoragePath)
                .select()
              
              if (updateError) {
                return { equipmentId, success: false, error: updateError.message }
              }
              
              insertError = null
            } catch (updateException) {
              return { equipmentId, success: false, error: updateException instanceof Error ? updateException.message : 'Update exception' }
            }
          } else {
            return { equipmentId, success: false, error: insertError.message }
          }
        }

        return { equipmentId, success: true, alreadyLinked: false }
      } catch (error) {
        return { equipmentId, success: false, error: error instanceof Error ? error.message : 'Unknown error' }
      }
    })

    const results = await Promise.all(linkPromises)
    const successful = results.filter(r => r.success).length
    const failed = results.filter(r => !r.success).length

    return {
      success: failed === 0,
      linkedCount: successful,
      error: failed > 0 ? `Failed to link to ${failed} equipment` : undefined
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

export const deleteDocument = async (documentId: string): Promise<UploadResult> => {
  try {
    if (import.meta.env.DEV) console.log('🗑️ Deleting document:', documentId)

    // Get document record first
    const { data: document, error: fetchError } = await supabase
      .from('equipment_documents')
      .select('*')
      .eq('id', documentId)
      .single()

    if (fetchError || !document) {
      return { success: false, error: 'Document not found' }
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from(DOCUMENT_CONFIG.BUCKET_NAME)
      .remove([document.storage_path])

    if (storageError) {
      console.warn('⚠️ Storage deletion failed:', storageError)
      // Continue with database deletion even if storage fails
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('equipment_documents')
      .delete()
      .eq('id', documentId)

    if (dbError) {
      return { success: false, error: `Database error: ${dbError.message}` }
    }

    if (import.meta.env.DEV) console.log('✅ Document deleted successfully')
    return { success: true }

  } catch (error) {
    console.error('💥 Document delete error:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : ERROR_MESSAGES.UNKNOWN_ERROR
    }
  }
}

// Air Liquide Certificate Mapping for Chain Slings
const AIR_LIQUIDE_CERTIFICATE_MAP: Record<string, string> = {
  // Batch format: "batch_number" or "batch_number-suffix" -> certificate
  '1302951': 'ESN-13-20095.pdf',
  '1103669-0020': 'ESN-10-21602.pdf', 
  '1208614': 'ESN-12-22469.pdf',
  '1308068': 'ESN-13-21334.pdf',
  '1402110-1': 'N140060A.pdf',  // Range 1-200
  '1402110-2': 'N140060B.pdf',  // Range 2-30
  '1103669-1': 'ESN-10-21601.pdf', // Range 1-*
  '1103669-3': 'ESN-11-21409.pdf'  // Range 3-*
};

const findSharedCertificate = async (serialNumber: string, companyId: string): Promise<EquipmentDocument | null> => {
  try {
    // Get company info
    const { data: company } = await supabase
      .from('companies')
      .select('name')
      .eq('id', companyId)
      .single();
      
    if (import.meta.env.DEV) console.log('🏢 Company data:', company);
      
    if (!company) {
      if (import.meta.env.DEV) console.log('❌ No company found for ID:', companyId);
      return null;
    }
    
    // Check if company name contains "air liquide" (case insensitive)
    const companyName = company.name.toLowerCase();
    if (!companyName.includes('air liquide')) {
      if (import.meta.env.DEV) console.log('❌ Not Air Liquide company:', company.name);
      return null; // Only for Air Liquide
    }
    
    if (import.meta.env.DEV) console.log('✅ Air Liquide company confirmed:', company.name);
    
    if (import.meta.env.DEV) console.log('🔍 Looking for shared certificate for serial:', serialNumber);
    
    // Parse serial number patterns - handle various formats with flexible parsing
    let certificateFile: string | null = null;
    
    // Normalize the serial number by removing extra spaces and standardizing separators
    const normalizedSerial = serialNumber.trim().replace(/\s+/g, ' ');
    
    if (import.meta.env.DEV) console.log('🔍 Normalized serial:', normalizedSerial);
    
    // Pattern matching with flexible parsing:
    // "1103669/1-78", "1103669 / 1-78", "1103669 1 78", "1103669 1-78"
    // "1302951 1 to 100", "1103669-0020 1 to 50"
    
    let batch: string = '';
    let rangePrefix: string = '';
    
    // Try to extract batch and range using multiple patterns
    
    // Pattern 1: Contains "/" (with or without spaces)
    // "1103669/1-78", "1103669 / 1-78", "1103669 /1-78"
    if (normalizedSerial.includes('/')) {
      const parts = normalizedSerial.split('/').map(p => p.trim());
      batch = parts[0];
      
      if (parts[1]) {
        // Extract range prefix from "1-78", "1 78", "1"
        const rangePart = parts[1].replace(/[-\s]+/, ' ').trim();
        rangePrefix = rangePart.split(' ')[0];
      }
    }
    // Pattern 2: Space-separated numbers
    // "1103669 1 78", "1302951 1 to 100", "1103669 1-78"
    else {
      const parts = normalizedSerial.split(' ').filter(p => p.length > 0);
      
      if (parts.length >= 2) {
        batch = parts[0];
        
        // Second part might be range prefix
        const secondPart = parts[1];
        
        // Handle formats like "1-78" or just "1"
        if (secondPart.includes('-')) {
          rangePrefix = secondPart.split('-')[0];
        } else if (!isNaN(parseInt(secondPart))) {
          rangePrefix = secondPart;
        }
      } else if (parts.length === 1) {
        // Just the batch number
        batch = parts[0];
      }
    }
    
    if (import.meta.env.DEV) console.log('🎯 Parsed - Batch:', batch, 'Range:', rangePrefix);
    
    // Try different lookup strategies
    if (batch && rangePrefix) {
      // Try with range first: "1103669-1"
      const lookupKey = `${batch}-${rangePrefix}`;
      certificateFile = AIR_LIQUIDE_CERTIFICATE_MAP[lookupKey];
      
      if (import.meta.env.DEV) console.log('🔍 Trying lookup key:', lookupKey, '→', certificateFile);
    }
    
    // Fallback to batch only: "1103669"
    if (!certificateFile && batch) {
      certificateFile = AIR_LIQUIDE_CERTIFICATE_MAP[batch];
      
      if (import.meta.env.DEV) console.log('🔍 Trying batch key:', batch, '→', certificateFile);
    }
    
    if (!certificateFile) {
      if (import.meta.env.DEV) console.log('❌ No certificate mapping found for:', serialNumber);
      return null;
    }
    
    const sharedPath = `air_liquide/shared/sling/${certificateFile}`;
    
    if (import.meta.env.DEV) console.log('🎯 Found certificate mapping:', sharedPath);
    
    // Check if file exists in storage
    const { data: fileExists, error: checkError } = await supabase.storage
      .from(DOCUMENT_CONFIG.BUCKET_NAME)
      .list('air_liquide/shared/sling/', {
        search: certificateFile.replace('.pdf', '')
      });
      
    if (checkError || !fileExists?.length) {
      if (import.meta.env.DEV) console.log('❌ Certificate file not found in storage:', sharedPath);
      return null;
    }
    
    // Create document object for the shared certificate
    const sharedDocument: EquipmentDocument = {
      id: `shared_cert_${serialNumber.replace(/[^a-zA-Z0-9]/g, '_')}`,
      equipment_id: '', // Will be set by caller
      document_type: 'certificate',
      file_name: certificateFile,
      storage_path: sharedPath,
      version: '1.0',
      is_current: true,
      created_at: new Date().toISOString(),
      file_size: fileExists[0]?.metadata?.size || 0,
      mime_type: 'application/pdf'
    };
    
    if (import.meta.env.DEV) console.log('✅ Created shared certificate document:', sharedDocument);
    return sharedDocument;
    
  } catch (error) {
    console.error('❌ Error finding shared certificate:', error);
    return null;
  }
};

export const listDocuments = async (equipmentId: string): Promise<{
  success: boolean
  documents?: EquipmentDocument[]
  error?: string
}> => {
  try {
    if (import.meta.env.DEV) console.log('📋 Listing documents for equipment:', equipmentId)

    // Get equipment details for shared certificate lookup
    const { data: equipment } = await supabase
      .from('equipment')
      .select('serial_number, company_id')
      .eq('id', equipmentId)
      .single();

    // Use the RPC function to get documents with profile information
    const { data, error } = await supabase.rpc('get_equipment_documents_with_profiles', {
      p_equipment_id: equipmentId
    })

    // Post-process documents to handle virtual storage paths
    if (data && Array.isArray(data)) {
      const processedDocuments = data.map((doc: any) => {
        // If storage path contains # (virtual path), extract the real path
        if (doc.storage_path && doc.storage_path.includes('#')) {
          const realPath = doc.storage_path.split('#')[0]
          return {
            ...doc,
            storage_path: realPath, // Use real path for file access
            _virtual_path: doc.storage_path // Keep original for reference
          }
        }
        return doc
      })
      
      if (import.meta.env.DEV) console.log('📋 Processed documents with virtual paths:', {
        total: processedDocuments.length,
        virtualPaths: processedDocuments.filter(d => d._virtual_path).length
      })
      
      // Continue with original logic using processed documents
      data.splice(0, data.length, ...processedDocuments)
    }

    if (error) {
      console.warn('⚠️ Database query failed, trying storage fallback:', error.message)
      
      // Fallback: try to get documents from storage
      // We'll need company name to construct storage path
      const { data: equipmentData } = await supabase
        .from('equipment')
        .select(`
          serial_number,
          owner_id,
          companies!owner_id (
            name
          )
        `)
        .eq('id', equipmentId)
        .single()

      if (equipmentData) {
        const customerName = (equipmentData.companies as any)?.[0]?.name || 'unknown'
        const sanitizedCustomer = sanitizeForFolderName(customerName)
        
        // Try new path format first: {company}/{equipment_uuid}/
        const uuidFolderPath = [sanitizedCustomer, equipmentId].join(DOCUMENT_CONFIG.FOLDER_SEPARATOR)
        
        if (import.meta.env.DEV) console.log('📁 Trying storage fallback with UUID path:', uuidFolderPath)
        
        let { data: storageFiles, error: storageError } = await supabase.storage
          .from(DOCUMENT_CONFIG.BUCKET_NAME)
          .list(uuidFolderPath)

        // If no files found with UUID path, try legacy serial number path for backward compatibility
        if ((!storageFiles || storageFiles.length === 0) && equipmentData.serial_number) {
          const sanitizedSerial = sanitizeForFolderName(equipmentData.serial_number)
          const legacyFolderPath = [sanitizedCustomer, sanitizedSerial].join(DOCUMENT_CONFIG.FOLDER_SEPARATOR)
          
          if (import.meta.env.DEV) console.log('📁 No files with UUID, trying legacy serial path:', legacyFolderPath)
          
          const legacyResult = await supabase.storage
            .from(DOCUMENT_CONFIG.BUCKET_NAME)
            .list(legacyFolderPath)
          
          if (legacyResult.data && legacyResult.data.length > 0) {
            storageFiles = legacyResult.data
            storageError = legacyResult.error
          }
        }

        if (storageError) {
          return { success: false, error: `Database and storage both failed: ${error.message}` }
        }

        // Convert storage files to document format
        const documents: EquipmentDocument[] = (storageFiles || []).map(file => ({
          id: `storage_${file.name}`, // Fake ID for storage-only files
          equipment_id: equipmentId,
          document_type: 'Annet',
          file_name: file.name,
          storage_path: `${uuidFolderPath}/${file.name}`,
          file_size: file.metadata?.size || 0,
          mime_type: file.metadata?.mimetype || 'application/octet-stream',
          version: '1.0',
          expiration_date: undefined,
          is_current: true,
          created_at: file.created_at || new Date().toISOString(),
          uploaded_by: undefined
        }))

        if (import.meta.env.DEV) console.log('✅ Storage fallback found', documents.length, 'documents')
        return { success: true, documents }
      }
      
      return { success: false, error: error.message }
    }

    if (import.meta.env.DEV) console.log('✅ Found', data?.length || 0, 'documents')
    
    let documents = data || [];
    
    // Check for shared certificates if equipment data is available
    if (import.meta.env.DEV) console.log('🔍 Equipment data for shared cert check:', equipment);
    
    if (equipment?.serial_number && equipment?.company_id) {
      // Check if there's already a certificate document
      const hasCertificate = documents.some((doc: any) => doc.document_type === 'certificate');
      
      if (import.meta.env.DEV) console.log('📋 Has existing certificate:', hasCertificate);
      if (import.meta.env.DEV) console.log('📋 Current documents:', documents.map((d: any) => d.document_type));
      
      if (!hasCertificate) {
        if (import.meta.env.DEV) console.log('🔍 Checking for shared certificate...');
        const sharedCert = await findSharedCertificate(equipment.serial_number, equipment.company_id);
        if (sharedCert) {
          sharedCert.equipment_id = equipmentId;
          documents.push(sharedCert);
          if (import.meta.env.DEV) console.log('✅ Added shared certificate:', sharedCert.file_name);
        } else {
          if (import.meta.env.DEV) console.log('❌ No shared certificate found');
        }
      } else {
        if (import.meta.env.DEV) console.log('⏭️ Skipping shared cert check - certificate already exists');
      }
    } else {
      if (import.meta.env.DEV) console.log('❌ Missing equipment data for shared cert check');
    }
    
    return { success: true, documents }

  } catch (error) {
    console.error('💥 List documents error:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : ERROR_MESSAGES.LIST_FAILED
    }
  }
}

// Get documents by type for better organization
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const _getDocumentsByType = async (equipmentId: string): Promise<{
  success: boolean
  documentsByType?: Record<string, EquipmentDocument[]>
  error?: string
}> => {
  try {
    const result = await listDocuments(equipmentId)
    
    if (!result.success || !result.documents) {
      return result
    }

    const documentsByType = result.documents.reduce((acc, doc) => {
      if (!acc[doc.document_type]) {
        acc[doc.document_type] = []
      }
      acc[doc.document_type].push(doc)
      return acc
    }, {} as Record<string, EquipmentDocument[]>)

    return { success: true, documentsByType }

  } catch (error) {
    console.error('💥 Get documents by type error:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : ERROR_MESSAGES.LIST_FAILED
    }
  }
}

// Test database access for equipment_documents table
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const _testDatabaseAccess = async (equipmentId: string): Promise<{
  success: boolean
  message: string
  canRead?: boolean
  canWrite?: boolean
}> => {
  try {
    if (import.meta.env.DEV) console.log('🧪 Testing database access for equipment_documents table...')
    if (import.meta.env.DEV) console.log('🔍 Equipment ID:', equipmentId)
    
    // Test read access
    if (import.meta.env.DEV) console.log('📖 Testing read access...')
    const { data: readData, error: readError } = await supabase
      .from('equipment_documents')
      .select('id')
      .eq('equipment_id', equipmentId)
      .limit(1)

    const canRead = !readError
    if (readError) {
      console.error('❌ Read access failed:', readError)
    } else {
      if (import.meta.env.DEV) console.log('✅ Read access successful, found', readData?.length || 0, 'documents')
    }

    // Test write access (dry run)
    if (import.meta.env.DEV) console.log('✏️ Testing write access...')
    const { error: writeError } = await supabase
      .from('equipment_documents')
      .insert({
        equipment_id: equipmentId,
        document_type: 'Annet',
        file_name: 'test_access_check.txt',
        storage_path: 'test/access/check.txt',
        file_size: 0,
        mime_type: 'text/plain',
        version: '1.0',
        is_current: true
      })
      .select()

    // Immediately delete the test record if insert succeeded
    if (!writeError) {
      if (import.meta.env.DEV) console.log('✅ Write access test successful, cleaning up...')
      await supabase
        .from('equipment_documents')
        .delete()
        .eq('equipment_id', equipmentId)
        .eq('file_name', 'test_access_check.txt')
    } else {
      console.error('❌ Write access failed:', writeError)
    }

    const canWrite = !writeError

    return {
      success: canRead && canWrite,
      message: `Read: ${canRead ? '✅' : '❌'}, Write: ${canWrite ? '✅' : '❌'}`,
      canRead,
      canWrite
    }

  } catch (error) {
    console.error('💥 Database access test error:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown database access error'
    }
  }
}

// Download a document file
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const _downloadDocument = async (doc: EquipmentDocument): Promise<{
  success: boolean
  error?: string
}> => {
  try {
    if (import.meta.env.DEV) console.log('⬇️ Downloading document:', doc.file_name)
    
    // Get the file from Supabase storage
    const { data, error } = await supabase.storage
      .from(DOCUMENT_CONFIG.BUCKET_NAME)
      .download(doc.storage_path)

    if (error) {
      console.error('❌ Download error:', error)
      return { success: false, error: `Download failed: ${error.message}` }
    }

    if (!data) {
      return { success: false, error: 'No file data received' }
    }

    // Create a blob and download link
    const blob = new Blob([data], { type: doc.mime_type || 'application/octet-stream' })
    const url = window.URL.createObjectURL(blob)
    
    // Create temporary download link
    const link = document.createElement('a')
    link.href = url
    link.download = doc.file_name
    document.body.appendChild(link)
    link.click()
    
    // Cleanup
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    if (import.meta.env.DEV) console.log('✅ Download successful:', doc.file_name)
    return { success: true }

  } catch (error) {
    console.error('💥 Download error:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown download error'
    }
  }
}

// Get document URL for preview/viewing
export const _getDocumentViewUrl = async (doc: EquipmentDocument): Promise<{
  success: boolean
  url?: string
  error?: string
}> => {
  try {
    if (import.meta.env.DEV) console.log('👁️ Getting view URL for document:', doc.file_name, 'type:', doc.mime_type)
    
    // Get signed URL for viewing (expires in 1 hour)
    // For PDFs, we need to specify the content type for Chrome compatibility
    const options: any = {}
    
    // Add content type override for PDFs to ensure Chrome displays them properly
    if (doc.mime_type?.toLowerCase() === 'application/pdf') {
      // Note: Supabase doesn't directly support ResponseContentType like AWS S3
      // But we can try to ensure the original file was uploaded with correct content type
      if (import.meta.env.DEV) console.log('📄 PDF detected - ensuring proper content type headers')
    }
    
    // Check if storage_path has bucket prefix (format: bucket:path)
    let bucketName = DOCUMENT_CONFIG.BUCKET_NAME
    let storagePath = doc.storage_path
    
    // Handle virtual paths (with #equipmentId suffix)
    if (storagePath.includes('#')) {
      storagePath = storagePath.split('#')[0]
    }
    
    // Handle bucket prefix (format: bucket:path)
    if (storagePath.includes(':') && !storagePath.startsWith('http')) {
      const [bucket, ...pathParts] = storagePath.split(':')
      bucketName = bucket
      storagePath = pathParts.join(':')
    }
    
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(storagePath, 3600, options) // 1 hour expiry

    if (error) {
      console.error('❌ Error getting signed URL:', error)
      return { success: false, error: `Could not get view URL: ${error.message}` }
    }

    if (!data?.signedUrl) {
      return { success: false, error: 'No signed URL received' }
    }

    if (import.meta.env.DEV) console.log('✅ Signed URL generated successfully')
    
    // For PDFs, we can try appending content-type parameter
    let finalUrl = data.signedUrl
    if (doc.mime_type?.toLowerCase() === 'application/pdf') {
      // Try to add content-type parameter to help Chrome
      const separator = finalUrl.includes('?') ? '&' : '?'
      finalUrl = `${finalUrl}${separator}response-content-type=application%2Fpdf`
      if (import.meta.env.DEV) console.log('📄 Added content-type parameter for PDF')
    }
    
    return { success: true, url: finalUrl }

  } catch (error) {
    console.error('💥 Get view URL error:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error getting view URL'
    }
  }
}

// Preview/view a document (opens in new tab)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const _previewDocument = async (doc: EquipmentDocument): Promise<{
  success: boolean
  error?: string
}> => {
  try {
    if (import.meta.env.DEV) console.log('👁️ Previewing document:', doc.file_name)
    
    const result = await _getDocumentViewUrl(doc)
    
    if (!result.success || !result.url) {
      return { success: false, error: result.error || 'Could not get view URL' }
    }

    // Open in new tab
    window.open(result.url, '_blank')
    
    if (import.meta.env.DEV) console.log('✅ Document opened for preview')
    return { success: true }

  } catch (error) {
    console.error('💥 Preview error:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown preview error'
    }
  }
}

// Test function with simplified bucket access test
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const _testBucketAccess = async (): Promise<{
  success: boolean
  message: string
}> => {
  try {
    if (import.meta.env.DEV) console.log('🧪 Testing bucket access...')
    
    // First check if user is authenticated
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError) {
      console.error('❌ Auth error:', authError)
      return { success: false, message: `Authentication error: ${authError.message}` }
    }
    
    if (!user) {
      if (import.meta.env.DEV) console.log('❌ No authenticated user')
      return { success: false, message: 'User not authenticated' }
    }
    
    if (import.meta.env.DEV) console.log('✅ User authenticated:', user.email)
    
    // Try to list files in the root of the bucket
    const { data: files, error: filesError } = await supabase.storage
      .from(DOCUMENT_CONFIG.BUCKET_NAME)
      .list('', { limit: 1 })

    if (filesError) {
      console.error('❌ Error accessing bucket:', filesError)
      return { success: false, message: `Cannot access bucket '${DOCUMENT_CONFIG.BUCKET_NAME}': ${filesError.message}` }
    }

    if (import.meta.env.DEV) console.log('✅ Bucket access successful - found', files?.length || 0, 'items in root')
    return { 
      success: true, 
      message: `Bucket '${DOCUMENT_CONFIG.BUCKET_NAME}' is accessible`
    }

  } catch (error) {
    console.error('💥 Bucket test error:', error)
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error testing bucket access'
    }
  }
} 